import { useEffect, useRef, useState } from 'react'

import { useToast } from '../components/radix'

import { removeItemFulfillmentMethod } from '../utils/cartFulfillment'
import { findSameDeliveryLines } from '../utils/cartLineMerge'
import type { CartItem } from '../types'
import type {
  CartItemResponse,
  MergeCartLineRequest,
  MergeCartLinesResponse,
} from '../auth/services/cart-api'

// Long enough to read why a line disappeared from the basket.
const MERGED_TOAST_MS = 5000

interface UseMergeSameDeliveryLinesOptions {
  // Basket lines whose live stock has loaded (others are left alone).
  items: ReadonlyArray<CartItem>
  getMethod: (item: CartItem) => 'pickup' | 'delivery'
  // False while live stock is loading or refetching, or the page is busy.
  isReady: boolean
  // A line that is being changed or saved right now is never merged.
  isLineBusy: (itemId: string) => boolean
  mergeCartLines: (lines: MergeCartLineRequest[]) => Promise<MergeCartLinesResponse>
  onMerged?: (keptItem: CartItemResponse, removedItemIds: string[]) => void
}

/**
 * Merge basket lines of one product that the supplier delivers from the
 * same branch into one line, once live stock says so, and tell the
 * engineer. Each set of lines is tried once per visit; a refused merge
 * leaves the lines as they were.
 *
 * @returns isSettling — a merge is due or running, or the page is still
 *   reloading stock after one; and unmergedProductNames — products whose
 *   lines are still split because their merge was refused or failed.
 *   Checkout holds Place order while either is set.
 */
export function useMergeSameDeliveryLines({
  items,
  getMethod,
  isReady,
  isLineBusy,
  mergeCartLines,
  onMerged,
}: UseMergeSameDeliveryLinesOptions): {
  isSettling: boolean
  unmergedProductNames: string[]
} {
  const { addToast } = useToast()
  const attemptedRef = useRef<Set<string>>(new Set())
  const [failedSignatures, setFailedSignatures] = useState<ReadonlySet<string>>(new Set())
  const [mergesInFlight, setMergesInFlight] = useState(0)
  const [hasMerged, setHasMerged] = useState(false)

  const sameDeliveryGroups = findSameDeliveryLines(items, getMethod)
  const dueGroups = isReady
    ? sameDeliveryGroups.filter(
        (group) =>
          !group.lines.some((line) => isLineBusy(line.item_id)) &&
          !attemptedRef.current.has(signatureOf(group.lines)),
      )
    : []
  // Not retried automatically (a refused merge would fail again); the
  // engineer changes the lines in the basket, which makes a new signature.
  const unmergedProductNames = sameDeliveryGroups
    .filter((group) => failedSignatures.has(signatureOf(group.lines)))
    .map((group) => group.productName)

  useEffect(() => {
    for (const group of dueGroups) {
      const signature = signatureOf(group.lines)
      if (attemptedRef.current.has(signature)) continue
      attemptedRef.current.add(signature)
      setMergesInFlight((count) => count + 1)
      mergeCartLines(group.lines)
        .then(({ item, removed_item_ids: removedItemIds }) => {
          removedItemIds.forEach(removeItemFulfillmentMethod)
          setHasMerged(true)
          addToast(
            `${item.quantity.toLocaleString()} × ${group.productName}, all delivered from ${group.deliveringBranchName}`,
            'info',
            MERGED_TOAST_MS,
            undefined,
            'Combined into one line',
          )
          onMerged?.(item, removedItemIds)
        })
        .catch(() => {
          // Left as separate lines; reported to the caller as unmerged.
          setFailedSignatures((previous) => new Set(previous).add(signature))
        })
        .finally(() => setMergesInFlight((count) => count - 1))
    }
  })

  return {
    isSettling: dueGroups.length > 0 || mergesInFlight > 0 || (hasMerged && !isReady),
    unmergedProductNames,
  }
}

const signatureOf = (lines: ReadonlyArray<MergeCartLineRequest>): string =>
  lines
    .map((line) => line.item_id)
    .sort()
    .join(',')
